import {Component, OnInit, ViewChild, Input, Output, EventEmitter, AfterViewInit, Injector, OnChanges, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl} from '@angular/forms';
import {CompanyListModalComponent} from '../company/CompanyListModalComponent';
import {PaymentProcessingService} from '../../../services/PaymentProcessingService';
import {startOf, endOf, nowDate} from '../../../utilities/date-util';
import {
    PagedListOfCompanyMailingReport,
    PagedListOfProcessingReport, PledgeReportParams,
    PledgeStatusType,
    ShiftPledgeReportSummary
} from '../../../services/api.client';
import {PromptComponent} from '../../shared/dialogs/PromptComponent';
import {Pledge} from '../../../services/api.client';
import {delay, take} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {CompanySelectedEvent} from '../../shared/events/company-selected-event';
import {NotificationService} from '../../../services/NotificationService';
import {SecurityService} from '../../../services/SecurityService';
import {
    buildPendingPledgesDetailParams,
    CompanyLock,
    normalizeReportDateRange,
    resolveCompanyLock
} from './pending-pledges-detail-params';


@Component({
    selector: '[paymentProcessingForm]',
    templateUrl: './PaymentProcessingFormComponent.html',
    styleUrls: ['./PaymentProcessingFormComponent.scss'],
    standalone: false
})

export class PaymentProcessingFormComponent implements OnInit, AfterViewInit, OnChanges {

    public form = this.fb.group({
        entryDate: [null],
        startDate: [null],
        endDate: [null],
        displayPledgeStatus: new FormControl<PledgeStatusType>(PledgeStatusType.Confirmed),
        shiftPledgeStatus: [null],
        pledgeTypeId: [null],
        pledgeTypeIds: [null],
        companyId: [''],
    });

    public entryDate = this.form.get('entryDate') as FormControl;
    public startDate = this.form.get('startDate') as FormControl;
    public endDate = this.form.get('endDate') as FormControl;
    public displayPledgeStatus = this.form.get('displayPledgeStatus') as FormControl;
    public shiftPledgeStatus = this.form.get('shiftPledgeStatus') as FormControl;
    public pledgeTypeId = this.form.get('pledgeTypeId') as FormControl;
    public pledgeTypeIds = this.form.get('pledgeTypeIds') as FormControl;
    public companyId = this.form.get('companyId') as FormControl;

    constructor(private fb: FormBuilder, private _reportService: PaymentProcessingService, private injector: Injector) {

    }

    shiftPledgeStatusDisplay: string;
    pledges: Pledge[] = new Array<Pledge>();
    @Output() pledgesChanged: EventEmitter<any> = new EventEmitter();

    @Input('isManual') isManual: boolean;
    @Input('showManualHeader') showManualHeader: boolean;
    @Input('showEntryDate') showEntryDate: boolean;
    @Input('showEndDate') showEndDate: boolean;
    @Input('showDateRange') showDateRange: boolean;
    @Input('showDisplayPledgeStatus') showDisplayPledgeStatus: boolean;
    @Input('showShiftPledgeStatus') showShiftPledgeStatus: boolean;
    @Input('showPledgeTypes') showPledgeTypes: boolean;
    @Input() showMultiChoicePledgeTypes: boolean;
    @Input() filterPledgeTypesByRole: boolean;
    @Input('showCompany') showCompany: boolean;
    // Pins the Company filter to a Fund Admin's own company (Bug 24463). Opt-in so the Process Payments
    // screens that share this form keep their picker.
    @Input() lockCompanyToFundAdmin: boolean;

    companyLock: CompanyLock = {locked: false, companyId: null, companyName: ''};

    get hasNoAssignedCompany(): boolean {
        return this.companyLock.locked && !this.companyLock.companyId;
    }

    @ViewChild(CompanyListModalComponent) companyListModal: CompanyListModalComponent;
    @ViewChild(PromptComponent) promptModal: PromptComponent;
    @Output('onShowPromptModal') _showPrompt = new EventEmitter();

    ngOnInit(): void {
        // Set form values based on input flags
        this.initializeFormValues();
        this.initializeCompanyLock();

        this.form.get('shiftPledgeStatus')?.valueChanges
            .pipe(delay(0)) // fixes expression has changed after it was checked.
            .subscribe(v => {
                this.shiftPledgeStatusDisplay = PledgeStatusType[v];
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Re-initialize form values when input flags change
        if (changes.showEntryDate || changes.showEndDate || changes.showDateRange) {
            this.initializeFormValues();
        }
    }

    private initializeFormValues(): void {
        // Set entryDate based on showEntryDate flag
        if (this.showEntryDate) {
            this.entryDate.setValue(startOf(nowDate(), 'year'));
        } else {
            this.entryDate.setValue(null);
        }

        // Set endDate based on showEndDate flag
        if (this.showEndDate) {
            this.endDate.setValue(endOf(nowDate(), 'day'));
        } else {
            this.endDate.setValue(null);
        }

        // Set startDate based on showDateRange flag
        if (this.showDateRange) {
            this.startDate.setValue(startOf(nowDate(), 'year'));
        } else {
            this.startDate.setValue(null);
        }
    }

    private initializeCompanyLock(): void {
        this.injector.get(SecurityService).currentUser$.pipe(take(1)).subscribe(user => {
            this.companyLock = resolveCompanyLock(user, this.lockCompanyToFundAdmin, this.showCompany);
            if (this.companyLock.companyId) {
                this.companyId.setValue(this.companyLock.companyId);
            }
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.form.get('displayPledgeStatus').reset(PledgeStatusType.Pending);
        });
    }

    submit(): any {
        return void 0;
    }

    submitPendingToPaid(): Observable<ShiftPledgeReportSummary[]> {
        const formValue = this.form.value;

        this.isManual = true;
        formValue.shiftPledgeStatus = PledgeStatusType.Paid;
        formValue.displayPledgeStatus = PledgeStatusType.Pending;

        const pledgeReportPrams = PledgeReportParams.fromJS(formValue);

        return this._reportService.buildShiftPledgeReport(pledgeReportPrams);
    }

    // existing infrastructure
    // submitManualPendingToPaid() {
    //     var form = this.form.value;
    //
    //     form.shiftPledgeStatus = PledgeStatusType.Paid;
    //     form.displayPledgeStatus = PledgeStatusType.Pending;
    //     return this._reportService.buildShiftPledgeReport(this.form.value);
    // }

    // new - for manual
    // getManualPendingToPaidPledges() {
    //     var form = this.form.value;
    //
    //     form.shiftPledgeStatus = PledgeStatusType.Paid;
    //     form.displayPledgeStatus = PledgeStatusType.Pending;
    //     let request = this._reportService.getManualPendingToPaidPledges(this.form.value);
    //     request.subscribe((data) => {
    //         this.pledges = data;
    //     }, (error) => {
    //     });
    //     this.pledgesChanged.emit(this.pledges);
    //     return request;
    // }

    submitConfirmedToPending(): Observable<ShiftPledgeReportSummary[]> {
        const formValue = this.form.value;
        formValue.shiftPledgeStatus = PledgeStatusType.Pending;
        formValue.displayPledgeStatus = PledgeStatusType.Confirmed;

        const pledgeReportParams = PledgeReportParams.fromJS(formValue);

        return this._reportService.buildShiftPledgeReport(pledgeReportParams);
    }

    submitPendingPledgesSummary(): Observable<ShiftPledgeReportSummary[]> {
        const formValue = this.form.value;
        const pledgeReportParams = PledgeReportParams.fromJS(formValue);

        return this._reportService.buildShiftPledgeReport(pledgeReportParams);
    }

    submitPayPledge(): boolean {
        // TODO: use right endpoint

        const formValue = this.form.value;
        formValue.shiftPledgeStatus = PledgeStatusType.Paid;
        formValue.displayPledgeStatus = PledgeStatusType.Pending;

        const pledgeReportParams = PledgeReportParams.fromJS(formValue);

        this.promptModal.open().subscribe(okClicked => {
            if (okClicked) {
                return this._reportService.changeUpdatePledgeStatus(pledgeReportParams);
            } else {
                return false;
            }
        });

        return false;
    }

    submitPendingPledgesDetail(offset?: number, limit?: number): Observable<PagedListOfProcessingReport> {
        if (this.hasNoAssignedCompany) {
            return;
        }
        const model = buildPendingPledgesDetailParams(this.form.value) as unknown as PledgeReportParams;
        if (!this.pledgeTypeIds.value.length) {
            this.injector.get(NotificationService).showError('Please select at least one fund.');
            return;
        }
        Object.assign(model, {offset, limit});
        return this._reportService.buildProcessingDetailReport(model);
    }

    downloadPendingPledgesDetail(): void {
        if (this.hasNoAssignedCompany) {
            return;
        }
        if (!this.pledgeTypeIds.value.length) {
            this.injector.get(NotificationService).showError('Please select at least one fund.');
            return;
        }
        // Same date window as Show Results, so the export holds the same pledges (end date included). The
        // constructor keeps the Date objects as-is; fromJS would re-parse them via toString() and drop the ms.
        const pledgeReportParams = new PledgeReportParams(buildPendingPledgesDetailParams(this.form.value));
        return this._reportService.buildProcessingDetailReportUrl(pledgeReportParams);
    }

    submitCompanyMailingLabel(offset?: number, limit?: number): Observable<PagedListOfCompanyMailingReport> {
        const model = this.normalizeDateRange(this.form.value);
        Object.assign(model, {offset, limit});
        return this._reportService.buildCompanyMailingLabels(model);
    }

    downloadCompanyMailingLabel(): void { // TODO
        return this._reportService.buildCompanyMailingLabelsUrl();
    }

    submitLockPledges(): boolean {

        const formValue = this.form.value;
        formValue.shiftPledgeStatus = PledgeStatusType.Pending;
        formValue.displayPledgeStatus = PledgeStatusType.Confirmed;

        const pledgeReportParams = PledgeReportParams.fromJS(formValue);

        this.promptModal.open().subscribe(okClicked => {
            if (okClicked) {
                return this._reportService.changeUpdatePledgeStatus(pledgeReportParams);
            } else {
                return false;
            }
        });

        return false;
    }

    // ---------
    private normalizeDateRange(model: any): any {
        return normalizeReportDateRange(model);
    }

    public companySelected(event: CompanySelectedEvent): void {
        if (event.noCompany) {
            this.companyId.setValue('');
        }
        this.companyId.setValue(event?.company && event.company?.id);
    }
}
